import React, { useContext } from "react";
import Loader from "Components/loader/Loader";
import Button from "Components/button/Button";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import { useHistory, useLocation } from "react-router-dom";
import ManagerCard from "Components/managerCard/ManagerCard";
import { alertError, alertSuccess } from "Utils/alerts";
import TranslatedString from "Components/TranslatedString";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import t from "Utils/translation";
import { Icon, addManager, notFound, plus, manager } from "Icons/index";
import withConfirmationModal from "Components/withConfirmationModal";
import cx from "classnames";
import s from "./listManagers.scss";
import { useApiMutation, useApiQuery } from "../../hooks";
import { AuthContext, MediaQueriesContext } from "../../App";

const ManagerListScreen = ({ openConfirmationModal }) => {
  const history = useHistory();
  const location = useLocation();
  const { isLoggedUserAdmin } = useContext(AuthContext);
  const { isMediaMobile } = useContext(MediaQueriesContext);

  const {
    data: managers,
    isLoading: areManagersLoading,
    pagination: managersPaginationInfo,
    getData: getManagers,
    updateData: updateManagers,
  } = useApiQuery("getManagers", {
    enablePagination: true,
  });

  const { mutate: changeManagerStatus } = useApiMutation("changeManagerStatus", {
    optimistic: (code, status) =>
      updateManagers((prev) => prev.map((manager) => (manager.code === code ? { ...manager, status } : manager))),
  });

  const { mutate: deleteManager } = useApiMutation("deleteManager", {
    optimistic: (code) => updateManagers((prev) => prev.filter((manager) => manager.code !== code)),
  });

  const handleNavigation = (path, state = {}) => {
    history.push({
      pathname: path,
      state: {
        ...state,
        prevPagination: location.search,
      },
    });
  };

  const navigateToNewManager = () => {
    handleNavigation("/managers/new");
  };

  const navigateToManager = (code, customerCode = null) => {
    const nextUrl = isLoggedUserAdmin() ? `/customers/${customerCode}/managers/${code}` : `/managers/${code}`;
    handleNavigation(nextUrl);
  };

  const handleChangeManagerStatus = async (code, status, isCustomerDisabled) => {
    if (status === "enabled" && isCustomerDisabled) {
      alertError(t("alert.customer.disabled"));
      return;
    }

    try {
      await changeManagerStatus(code, status);
    } catch (error) {
      console.error("Failed to change manager status:", error);
      alertError(t("alert.error.generic"));
    }
  };

  const handleDeleteManager = async (code) => {
    try {
      await deleteManager(code);
      alertSuccess(t("alert.managerDeletedSuccess"));
    } catch (error) {
      console.error("Failed to delete manager:", error);
      alertError(t("alert.error.generic"));
    }
  };

  return (
    <div className={s.listManagers}>
      <div className={s.headerList}>
        <div className={cx("sectionTitle", s.titleList)}>
          <TranslatedString>managers.list.header.title</TranslatedString>
        </div>

        {!isMediaMobile && (
          <div className={cx("sectionDesc", s.descList)}>
            <TranslatedString>managers.list.header.dsc</TranslatedString>
          </div>
        )}

        <div className={s.actionsList}>
          <Button docsKey="managers" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
            button.docs
          </Button>
          <Button icon={plus} onClick={navigateToNewManager} onlyIcon={isMediaMobile}>
            button.addManager
          </Button>
        </div>
      </div>

      <div className={s.contentList}>
        <FullPaginationControls
          className={s.filters}
          paginationInfo={managersPaginationInfo}
          location={location}
          refreshList={getManagers}
          listFilters={["all", "enabled", "disabled"]}
          listFilterLegends={{
            all: <Icon svg={manager} width="0.75rem" height="0.75rem" />,
            enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
            disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
          }}
          showCustomerSelection={isLoggedUserAdmin()}
          isLoading={areManagersLoading}
          totals={managersPaginationInfo.totals}
        />

        {areManagersLoading ? (
          <Loader className={s.loader} />
        ) : !managers?.length ? (
          <div className={s.noFoundMsg}>
            <div className={s.wrapIllustration}>
              <Icon
                svg={
                  !location.query.query && !location.query.status && !location.query.customer_code
                    ? addManager
                    : notFound
                }
                width="10rem"
                height="10rem"
              />
              <div className={s.msgIllustration}>
                {!location.query.query && !location.query.status && !location.query.customer_code
                  ? t("table.row.add.msg.manager")
                  : t("infoMsg.noRecords.msg")}
              </div>
              <div className={s.descIllustration}>
                {!location.query.query && !location.query.status && !location.query.customer_code
                  ? t("table.row.add.desc.manager")
                  : t("infoMsg.noRecords.desc")}
              </div>
            </div>
          </div>
        ) : (
          <div className={s.table}>
            {managers.map((manager) => (
              <ManagerCard
                {...manager}
                key={manager.code}
                showManager={navigateToManager}
                changeManagerStatus={handleChangeManagerStatus}
                delete={() =>
                  openConfirmationModal(
                    manager,
                    handleDeleteManager,
                    <TranslatedString subs={{ name: manager.fullname }}>modal.delete.manager.text</TranslatedString>
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default withConfirmationModal(ManagerListScreen, {
  title: <TranslatedString>modal.delete.manager.title</TranslatedString>,
  buttonLabel: "button.delete",
});
